import React from "react";
import { map } from "lodash";
import styled from "styled-components";
import {
  GlossyThemeProvider,
  Radius,
  background,
  dominant,
  form,
  transparentize,
} from "@glossy";
import Selector from "@radarly/components/panels/card-edition/chart-selector";
import { useFacetsFromContext } from "@radarly/core/facets/use-cases";
import { useFeature } from "@radarly/core/features/use-cases";
import { useCurrentProjectFocusesId } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { useCurrentWorkspaceId } from "@radarly/core/workspace/use-cases";
import { tr } from "@radarly/i18n";
import { Card } from "@radarly/models/cards";
import { getDatavizSections } from "@radarly/models/cards/dataviz";
import { setCardDataviz } from "@radarly/models/cards/editor";
import { DatavizName, getDefaultDataviz } from "@radarly/models/dataviz";
import Illustration from "@radarly/shared/cards/edition/illustration";

interface SharedPanelsCardEditionSidebarDatavizProps {
  card: Card;
  setCard: (card: Card) => void;
  closePanel: () => void;
}

const Item = styled.div<{ single: boolean; selected: boolean }>`
  height: 82px;
  padding: 12px;
  border-radius: ${Radius.Medium};
  cursor: pointer;
  flex: 0 0 ${(props) => (props.single ? "100%" : "calc(50% - 12px)")};
  background-color: ${background};
  box-shadow: ${(props) =>
    props.selected && `0 0 0 2px ${dominant(props)} inset`};
  :hover {
    background-color: ${(props) =>
      !props.selected && transparentize(0.8, form(props, "primary"))};
  }
`;

const SharedPanelsCardEditionSidebarDataviz = ({
  card,
  setCard,
  closePanel,
}: SharedPanelsCardEditionSidebarDatavizProps) => {
  const locale = useCurrentUserLocale();
  const facets = useFacetsFromContext();
  const treeMapEnabled = useFeature("treemap");
  const workspaceId = useCurrentWorkspaceId();
  const projectFocuses = useCurrentProjectFocusesId();
  return (
    <Selector
      title={tr(locale, "CUSTOM_PAGES::EDITOR::CHART_TYPE")}
      sections={map(
        getDatavizSections(card, facets, treeMapEnabled),
        (section) => ({
          ...section,
          values: map(section.values, (value) => ({ value })),
        })
      )}
      selected={card.dataviz.name}
      onClickBack={closePanel}
      renderItem={(single, item, selected, index) => (
        <GlossyThemeProvider name="tile" key={index}>
          <Item
            single={single}
            selected={item.value === selected}
            onClick={() =>
              setCard(
                setCardDataviz(
                  workspaceId,
                  projectFocuses,
                  card,
                  item.value as DatavizName
                )
              )
            }
          >
            <Illustration
              dataviz={getDefaultDataviz(item.value as DatavizName)}
              fragments={card.fragments}
            />
          </Item>
        </GlossyThemeProvider>
      )}
    />
  );
};

export default SharedPanelsCardEditionSidebarDataviz;
